import React, { createRef } from "react";
import PropTypes from "prop-types";
import Portal from "reactstrap/lib/Portal";
import RDatePicker from "react-datepicker";
import find from "lodash/find";
import get from "lodash/get";
import format from "date-fns/format";
import getDate from "date-fns/getDate";
import getYear from "date-fns/getYear";
import lastDayOfMonth from "date-fns/lastDayOfMonth";

import { isFunction, gatherClasses, hasProperty } from "../../lib/utils";
import InputFeedback from "../inputFeedback/inputFeedback";
import DateHeader from "./dateHeader";
import DateInput from "./datepickerInput";
import CustomDay from "./customDay";
import {
  getSanitizedDate,
  createCustomInput,
  normalizeHighlights,
  __DEFAULT_DATE_FORMAT__,
  __DEFAULT_TIME_FORMAT__
} from "./datepickerUtils";
import {
  DateType,
  HightlightDatesPropType,
  popperPlacements
} from "./inputPropTypes";

import "./react-datepicker.css";
import "./datepicker.scss";

const CalendarContainer = ({ children }) =>
  children ? <Portal>{children}</Portal> : null;

CalendarContainer.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
};

class DatePicker extends React.Component {
  constructor(props) {
    super(props);

    const { highlightDates } = this.props;

    this.state = {
      highlightedDates: normalizeHighlights(highlightDates),
      popperPosition: "fixed"
    };
    this.inputRef = createRef();
  }

  componentDidMount() {
    const { getInputRef } = this.props;

    if (isFunction(getInputRef)) {
      getInputRef(this.inputRef);
    }
    // Special case handling of the date picker rendered inside in a filter button
    if (this.isDescendantOfFilterButton()) {
      this.setState({ popperPosition: "absolute" });
    }
  }

  isDescendantOfFilterButton() {
    let element = this.inputRef.current;
    const filterBtnWrapperCls = "filter-button-modal-popover";

    if (element !== null) {
      // eslint-disable-next-line no-cond-assign
      while ((element = element.parentNode)) {
        if (
          element &&
          element.classList &&
          element.classList.contains(filterBtnWrapperCls)
        ) {
          return true;
        }
      }
    }

    return false;
  }

  static getDerivedStateFromProps(nextProps) {
    return {
      highlightedDates: normalizeHighlights(nextProps.highlightDates),
      value: nextProps.value
    };
  }

  getFormattedDate(date) {
    const { dateFormat } = this.props;
    return date ? format(date, dateFormat) : null;
  }

  setFieldTouched = () => {
    const { name, validationProps } = this.props;

    if (name && validationProps && validationProps.setFieldTouched) {
      validationProps.setFieldTouched(name, true, false);
    }
  };

  setFieldValue = date => {
    const { name, validationProps } = this.props;
    const formatted = this.getFormattedDate(date);

    if (name && validationProps && validationProps.setFieldValue) {
      // If formatted is null, we will send empty string instead to save
      // users from using .nullable there
      validationProps.setFieldValue(
        name,
        formatted === null ? "" : formatted,
        true
      );
    }
  };

  handleOnFocus = e => {
    const { onFocus } = this.props;

    if (isFunction(onFocus)) {
      onFocus.call(this, e);
    }
  };

  handleOnChange = date => {
    this.setFieldTouched();
    this.setFieldValue(date);
    this.callHandler("onChange", date);
  };

  handleOnMonthChange = date => {
    this.callHandler("onMonthChange", date);
  };

  handleOnYearChange = date => {
    this.callHandler("onYearChange", date);
  };

  callHandler(handler, date) {
    // eslint-disable-next-line react/destructuring-assignment
    if (hasProperty(this.props, handler) && isFunction(this.props[handler])) {
      const formatted = this.getFormattedDate(date);
      // eslint-disable-next-line react/destructuring-assignment
      this.props[handler].call(this, date, formatted);
    }
  }

  getExcludedDates() {
    const { excludeDates } = this.props;
    if (Array.isArray(excludeDates)) {
      return excludeDates.map(getSanitizedDate);
    }
    return undefined;
  }

  getCustomInput() {
    const { isClearable, isInvalid, customInput, ariaLabel } = this.props;
    const inputProps = {
      isClearable,
      isInvalid,
      ariaLabel
    };

    if (isFunction(customInput)) {
      const Comp = createCustomInput(customInput);
      return <Comp {...inputProps} inputRef={this.inputRef} />;
    }

    return <DateInput {...inputProps} inputRef={this.inputRef} />;
  }

  getCustomDay = (_, date) => {
    const { highlightedDates } = this.state;
    const highlight = find(highlightedDates, {
      localeStr: format(date, __DEFAULT_DATE_FORMAT__)
    });

    return <CustomDay date={date} highlight={highlight || false} />;
  };

  getCustomHeader = () => {
    const { startYear, endYear, minDate, maxDate } = this.props;
    return ({
      date,
      changeYear,
      changeMonth,
      decreaseMonth,
      increaseMonth,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled
    }) => (
      <DateHeader
        startYear={startYear}
        endYear={endYear + 1}
        minDate={getSanitizedDate(minDate)}
        maxDate={getSanitizedDate(maxDate)}
        date={date}
        changeYear={changeYear}
        changeMonth={changeMonth}
        decreaseMonth={decreaseMonth}
        increaseMonth={increaseMonth}
        prevMonthButtonDisabled={prevMonthButtonDisabled}
        nextMonthButtonDisabled={nextMonthButtonDisabled}
      />
    );
  };

  render() {
    const { popperPosition } = this.state;
    let { minDate, maxDate } = this.props;
    const {
      name,
      value,
      disableInputFeedback,
      validationProps,
      disabled,
      dateFormat,
      openToDate,
      minTime,
      maxTime,
      isInvalid,
      shouldCloseOnSelect,
      placement,
      startYear,
      endYear,
      placeholder,
      isClearable,
      dataTestId
    } = this.props;
    let dateInputValue = value;

    if (!minDate) {
      minDate = new Date(startYear, 0, 1);
    }

    if (!maxDate) {
      maxDate = new Date(
        endYear,
        11,
        getDate(lastDayOfMonth(new Date(endYear, 11, 1)))
      );
    }

    if (name && validationProps && validationProps.values) {
      dateInputValue = get(validationProps.values, name);
    }

    return (
      <div
        data-testid={dataTestId}
        className={gatherClasses(this.props, "oui-datepicker")}
      >
        <RDatePicker
          popperClassName="oui-datepicker-popper"
          selected={getSanitizedDate(dateInputValue)}
          disabled={disabled}
          isClearable={isClearable}
          dateFormat={dateFormat}
          onFocus={this.handleOnFocus}
          onChange={this.handleOnChange}
          onMonthChange={this.handleOnMonthChange}
          onYearChange={this.handleOnYearChange}
          openToDate={getSanitizedDate(openToDate)}
          minDate={getSanitizedDate(minDate)}
          maxDate={getSanitizedDate(maxDate)}
          minTime={getSanitizedDate(minTime)}
          maxTime={getSanitizedDate(maxTime)}
          shouldCloseOnSelect={shouldCloseOnSelect}
          customInput={this.getCustomInput()}
          renderCustomHeader={this.getCustomHeader()}
          renderDayContents={this.getCustomDay}
          popperContainer={popperPosition === "fixed" ? CalendarContainer : ""}
          popperPlacement={placement}
          disabledKeyboardNavigation
          placeholderText={placeholder}
          excludeDates={this.getExcludedDates()}
          onCalendarClose={this.setFieldTouched}
        />
        {!disableInputFeedback && name && validationProps && (
          <InputFeedback
            show={isInvalid}
            message={get(validationProps.errors, name)}
          />
        )}
      </div>
    );
  }
}

DatePicker.defaultProps = {
  name: null,
  className: "",
  startYear: 1990,
  endYear: getYear(new Date()),
  dateFormat: __DEFAULT_DATE_FORMAT__,
  timeFormat: __DEFAULT_TIME_FORMAT__,
  onChange: () => {},
  value: "",
  timeIntervals: 15,
  isInvalid: false,
  placement: "bottom-start",
  placeholder: null,
  isClearable: true,
  getInputRef: null,
  validationProps: null,
  disableInputFeedback: false,
  dataTestId: "orion-datepicker"
};

DatePicker.propTypes = {
  name: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.oneOfType([DateType, PropTypes.oneOf([null])]),
  placeholder: PropTypes.string,
  showTimePicker: PropTypes.bool,
  isInvalid: PropTypes.bool,
  disabled: PropTypes.bool,
  dateFormat: PropTypes.string,
  timeFormat: PropTypes.string,
  timeIntervals: PropTypes.number,
  openToDate: DateType,
  minDate: DateType,
  maxDate: DateType,
  minTime: DateType,
  maxTime: DateType,
  shouldCloseOnSelect: PropTypes.bool,
  highlightDates: HightlightDatesPropType,
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  onMonthChange: PropTypes.func,
  onYearChange: PropTypes.func,
  startYear: PropTypes.number,
  endYear: PropTypes.number,
  placement: popperPlacements,
  isClearable: PropTypes.bool,
  excludeDates: PropTypes.arrayOf(DateType),
  customInput: PropTypes.func,
  getInputRef: PropTypes.func,
  validationProps: PropTypes.object,
  disableInputFeedback: PropTypes.bool,
  dataTestId: PropTypes.string,
  ariaLabel: PropTypes.string
};

DatePicker.displayName = "OrionDatePicker";

export default DatePicker;
